import { useCallback, useEffect } from 'react';
import { useLoaderData } from '@remix-run/react';
import type { UIComponent } from '~/services/layout';
import type { loader } from '~/routes/_index';

export const useSubscribeToActionRefreshComponent = ({
  clientFeedApiEndpoint,
  fetchData,
  uiComponent,
}: {
  clientFeedApiEndpoint?: string | null;
  fetchData: (forceFresh: boolean) => void;
  uiComponent: UIComponent;
}) => {
  const { favoritesSyncedFeed } = useLoaderData<typeof loader>();
  const componentId: string = uiComponent.id;
  const source: string | undefined = uiComponent.data?.source;

  const matchesFeed = useCallback(
    (feedPath?: string) => {
      const base = (s?: string | null) =>
        s ? s.split('?')[0].replace(/\/+$/, '') : '';

      const target = base(feedPath);
      const decodedEndpoint = clientFeedApiEndpoint
        ? decodeURIComponent(clientFeedApiEndpoint)
        : '';

      return Boolean(
        target &&
          (base(source) === target ||
            (decodedEndpoint &&
              (decodedEndpoint.includes(`${target}?`) ||
                decodedEndpoint.includes(`${target}&`) ||
                decodedEndpoint.includes(`${target}"`) ||
                decodedEndpoint.endsWith(target))))
      );
    },
    [clientFeedApiEndpoint, source]
  );

  useEffect(() => {
    const listener = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;

      const matchesComponentId = Boolean(
        detail.componentId && detail.componentId === componentId
      );

      if (matchesComponentId || matchesFeed(detail.feedPath)) {
        fetchData(true);
      }
    };

    window.addEventListener('action:refreshComponent', listener);
    return () => {
      window.removeEventListener('action:refreshComponent', listener);
    };
  }, [fetchData, componentId, matchesFeed]);

  return Boolean(clientFeedApiEndpoint && matchesFeed(favoritesSyncedFeed));
};
